import { ReactNode } from 'react';
import { styled } from '@mui/material/styles';
import { CircularProgress } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { UseQueryResult } from '@tanstack/react-query';
import Button from '@/components/Button/Button';
import WarningTriangleIcon from '@/assets/warning-triangle.svg?react';

type QueryStateProps = {
  // Any refetch fits, so useInfiniteScroll results work too (pass the
  // first page as data).
  query: Pick<
    UseQueryResult,
    'data' | 'isPending' | 'isError' | 'isFetching' | 'failureCount'
  > & { refetch: () => unknown };
  errorMessage?: ReactNode;
  // Drops the error box frame when it renders inside a card that
  // already has one.
  embedded?: boolean;
  children: ReactNode;
};

// Renders children once the query has data; otherwise shows a loader
// (with a hint while retrying) or an error with a Retry action, so a
// failed request never looks like an empty result. Already loaded data
// is kept on screen when a background refetch fails.
const QueryState = ({
  query,
  errorMessage,
  embedded = false,
  children,
}: QueryStateProps) => {
  if (query.data !== undefined) {
    return <>{children}</>;
  }

  if (query.isPending || query.isFetching) {
    return (
      <Centered role="status" aria-label="Loading data">
        <CircularProgress />
        {query.failureCount > 1 ? (
          <Hint>
            <FormattedMessage
              id="queryStateRetrying"
              defaultMessage="Having trouble reaching the server, retrying…"
            />
          </Hint>
        ) : null}
      </Centered>
    );
  }

  if (query.isError) {
    return (
      <ErrorBox role="alert" $embedded={embedded}>
        <WarningTriangleIcon />
        <ErrorText>
          {errorMessage || (
            <FormattedMessage
              id="queryStateError"
              defaultMessage="We couldn't load this data. Check your connection and try again."
            />
          )}
        </ErrorText>
        <Button
          variant="outlined"
          onClick={() => query.refetch()}
          aria-label="Retry Button"
        >
          <FormattedMessage id="retry" defaultMessage="Retry" />
        </Button>
      </ErrorBox>
    );
  }

  return <>{children}</>;
};

const Centered = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(2),
  padding: theme.spacing(4),
}));

const Hint = styled('div')(({ theme }) => ({
  color: theme.palette.grey500,
  fontSize: 14,
}));

const ErrorBox = styled('div')<{ $embedded: boolean }>(
  ({ theme, $embedded }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(4),
    textAlign: 'center',
    ...($embedded
      ? {}
      : {
          marginTop: theme.spacing(2),
          borderRadius: theme.radii.sm,
          border: `1px solid ${theme.palette.grey200}`,
          background: '#FFFFFF',
        }),

    svg: {
      width: 32,
      height: 32,
    },
  }),
);

const ErrorText = styled('div')(({ theme }) => ({
  color: theme.palette.textPrimaryBlack,
  fontSize: 14,
  lineHeight: '20px',
}));

export default QueryState;
